/* 🎼 IAS HARMONY ENGINE v1 (Dial-in, 10-16). Pure music logic — no DOM. Browser: window.IASHarmony · Node: require().
   Truth comes from the TITLE pitch (F♯4, C#5, B♭4 (A#)…) + the harmony map's form factor / exclusion flags.
   LAWS (canon): register truth — intervals are named from the lowest sounding note (PAIRING_CANON M1) · three octaves of one
   pitch-class are ONE pitch, not a chord (M2) · 432 never marries 440 (cross-Hz law) · scale law — a DORIAN / MAJOR-scale
   instrument fixes the mode, so a target chord must live inside its scale · exclusions (map ex=1: Dorian, MAJOR×JP, collabs)
   are never suggested · completion gate — a finished triad is celebrated, never "completed" again (GUARD LAW 3) ·
   "true chord" = a real major or minor triad only; sus = open, no third yet.
   Suggestions always aim at ONE target chord (root · third · fifth) — never a loose cluster of keys. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.IASHarmony = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';
  var LETTER = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  var SHARP = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
  var FLAT = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];
  /* flat-side roots spell with flats (F, B♭, E♭, A♭, D♭ majors · D, G, C, F, B♭ minors) — enharmonic truth */
  var FLAT_MAJOR = { 5: 1, 10: 1, 3: 1, 8: 1, 1: 1 }, FLAT_MINOR = { 2: 1, 7: 1, 0: 1, 5: 1, 10: 1 };
  var IV = ['unison', 'minor second', 'major second', 'minor third', 'major third', 'perfect fourth', 'tritone',
    'perfect fifth', 'minor sixth', 'major sixth', 'minor seventh', 'major seventh'];
  var TRIADS = [ // order = preference when a set is ambiguous
    { q: 'major', iv: [0, 4, 7], name: 'major', trueChord: true },
    { q: 'minor', iv: [0, 3, 7], name: 'minor', trueChord: true },
    { q: 'sus4', iv: [0, 5, 7], name: 'sus4', trueChord: true },
    { q: 'sus2', iv: [0, 2, 7], name: 'sus2', trueChord: true },
    { q: 'dim', iv: [0, 3, 6], name: 'diminished', trueChord: true },
    { q: 'aug', iv: [0, 4, 8], name: 'augmented', trueChord: true }
  ];
  var SCALES = { dorian: [0, 2, 3, 5, 7, 9, 10], major: [0, 2, 4, 5, 7, 9, 11] };
  /* tie-break toward the canon chord set (Deck 10-16 + Chord Shelf): D, G, C major · A, E, F♯ minor */
  var CANON = { 2: 'major', 7: 'major', 0: 'major', 9: 'minor', 4: 'minor', 6: 'minor' };
  /* FINAL INFUSION ③ SHELF-FIRST: when harmony ranks two picks equal, the one with Ships Now stock (catalog row now:true) wins */
  function shelf(a, b) { return (b.now ? 1 : 0) - (a.now ? 1 : 0); }
  function canon(t) { return CANON[t.root] === t.q ? 0 : 1; }
  /* canon ranking (Pairing Law v2 + Deck 10-16): different key, same octave first → Pocket / smaller → octave twin last */
  var FORM_PREF = { drone: 0, pocket: 1, triple: 1, single: 2, ocarina: 2 };
  var NOT_A_NOTE = /expansion\s+pack|\bkit\b|hanger|\bstand\b|\bcase\b|\bbag\b|strap|gift\s*card/i;
  var LESSON = {
    unison: 'Unison: the same note twice. Fuller and stronger, but still one pitch. A chord needs different notes.',
    octave: 'Octave: the same note higher or lower. "One voice, twice as deep." Still one pitch-class, so not a chord yet.',
    root: 'Root: the note the chord is named after. Everything else is measured from it.',
    third: 'The third sets the mood: a minor third (3 half-steps) sounds soft and wistful, a major third (4) sounds bright.',
    fifth: 'The fifth: the open, steady pillar of the chord, "the open sky" of every drone tradition.',
    triad: 'A chord = three different notes stacked: root, third, fifth. F♯ + A♯ + C♯ = F♯ major.',
    sus: 'A suspended chord swaps the third for a 2nd or 4th: root, 2nd/4th and fifth. Open and floating, still a true chord.',
    cluster: 'Cluster: neighbouring notes rubbing together. A rich texture, but no single chord. Pick one chord to aim for.',
    scale: 'Scale flutes (Dorian, Major) each carry their own mode. Mixing modes is a melody choice, not a chord set.',
    hz: '432 Hz and 440 Hz instruments are tuned about a third of a half-step apart, so they beat against each other. Keep a set in one tuning.'
  };

  function parsePitch(title) {
    var m = String(title || '').trim().match(/^([A-Ga-g])\s*([#♯b♭]?)\s*(\d)/);
    if (!m) return null;
    var L = m[1].toUpperCase(), acc = m[2], oct = +m[3];
    var pc = LETTER[L] + (acc === '#' || acc === '♯' ? 1 : acc === 'b' || acc === '♭' ? -1 : 0);
    var midi = 12 * (oct + 1) + pc; pc = ((pc % 12) + 12) % 12;
    return { pc: pc, midi: midi, oct: oct, label: L + (acc === '#' ? '♯' : acc === 'b' ? '♭' : acc) + oct };
  }
  function hzOf(title) { var m = String(title || '').match(/\b(432|440)\b/); return m ? m[1] : ''; }
  function scaleOf(title) { var t = String(title || ''); return /DORIAN/i.test(t) ? 'dorian' : /MAJOR\s+Scale/i.test(t) ? 'major' : null; }

  /* one voice per cart unit: {handle,title,pitch,hz,scale,ff,ex} — unpitched / accessory items are ignored (counted elsewhere) */
  function voice(item, attrs) {
    var p = parsePitch(item.title), a = (attrs && attrs[item.handle]) || {};
    if (!p || a.ff === 'accessory' || NOT_A_NOTE.test(item.title)) return null;
    return { handle: item.handle, title: item.title, label: p.label, pc: p.pc, midi: p.midi, oct: p.oct,
      hz: hzOf(item.title) || a.hz || '', scale: scaleOf(item.title), ff: a.ff || 'drone', ex: +a.ex || 0 };
  }
  function noteName(pc) { return SHARP[((pc % 12) + 12) % 12]; }
  /* "A♯ (our B♭4)" — chord spelling first, our catalog name when it differs */
  function display(n) {
    if (!n.pick) return n.note;
    var cat = n.pick.label.replace(/\d+$/, '');
    return cat === n.note ? n.pick.label : n.note + ' (our ' + n.pick.label + ')';
  }
  function spell(pc, rootPc, quality) {
    var flat = quality === 'minor' || quality === 'dim' ? FLAT_MINOR[rootPc] : FLAT_MAJOR[rootPc];
    return (flat ? FLAT : SHARP)[((pc % 12) + 12) % 12];
  }
  function chordName(rootPc, q) {
    var t = TRIADS.filter(function (x) { return x.q === q; })[0];
    var r = spell(rootPc, rootPc, q);
    return q === 'sus4' || q === 'sus2' ? r + q : r + ' ' + t.name;
  }
  function triadPcs(rootPc, q) { return TRIADS.filter(function (x) { return x.q === q; })[0].iv.map(function (i) { return (rootPc + i) % 12; }); }
  function roleOf(pc, rootPc, q) {
    var d = ((pc - rootPc) % 12 + 12) % 12, iv = TRIADS.filter(function (x) { return x.q === q; })[0].iv;
    return d === 0 ? 'root' : d === iv[2] ? 'fifth' : d === iv[1] ? (q === 'sus4' ? 'fourth' : q === 'sus2' ? 'second' : 'third') : 'extra';
  }
  function uniq(a) { return a.filter(function (x, i) { return a.indexOf(x) === i; }); }

  /* split the cart by tuning: the main group = the larger one (tie → the first line's tuning) */
  function groupHz(voices) {
    var g = {}; voices.forEach(function (v) { (g[v.hz || '?'] = g[v.hz || '?'] || []).push(v); });
    var keys = Object.keys(g);
    keys.sort(function (a, b) { return g[b].length - g[a].length || (voices[0].hz === a ? -1 : 1); });
    return { main: keys.length ? g[keys[0]] : [], hz: keys[0] || '', mixed: keys.filter(function (k) { return k !== '?'; }).length > 1, other: keys.slice(1) };
  }

  /* CLASSIFY a set of voices (octave-independent identity, register-true naming) */
  function classify(voices) {
    var v = voices.slice().sort(function (a, b) { return a.midi - b.midi; });
    if (!v.length) return { kind: 'empty', label: '', trueChord: false, lesson: '' };
    var pcs = uniq(v.map(function (x) { return x.pc; })), bass = v[0];
    if (v.length === 1) return { kind: 'single', label: bass.label, root: bass.pc, trueChord: false, lesson: LESSON.root };
    if (pcs.length === 1) {
      var mids = uniq(v.map(function (x) { return x.midi; }));
      return mids.length === 1
        ? { kind: 'unison', label: (v.length === 2 ? 'Two ' : v.length + ' ') + noteName(bass.pc) + 's: the same note, ' + (v.length === 2 ? 'twice' : v.length + '×') + ' the depth', root: bass.pc, trueChord: false, lesson: LESSON.unison }
        : { kind: 'octave', label: noteName(bass.pc) + ' in ' + mids.length + ' heights', root: bass.pc, trueChord: false, lesson: LESSON.octave };
    }
    if (pcs.length === 2) {
      var other = v.filter(function (x) { return x.pc !== bass.pc; })[0];
      var d = other.midi - bass.midi, ic = d % 12, name = IV[ic] + (d > 12 ? ' (plus an octave)' : '');
      var lead = ic === 7 ? 'The open fifth, the oldest drone sound' : ic === 5 ? 'The perfect fourth, floating and open' :
        name.charAt(0).toUpperCase() + name.slice(1);
      return { kind: 'interval', interval: IV[ic], semis: d, label: lead + ': ' + other.label + ' over ' + bass.label,
        root: bass.pc, trueChord: false, lesson: ic === 7 || ic === 5 ? LESSON.fifth : ic === 3 || ic === 4 ? LESSON.third : LESSON.cluster };
    }
    var tri = findTriad(pcs, bass.pc);
    if (tri && pcs.length === 3) {
      return { kind: 'triad', quality: tri.q, root: tri.root, trueChord: tri.trueChord,
        label: '✨ ' + chordName(tri.root, tri.q) + ': a true chord',
        roles: v.map(function (x) { return { label: x.label, role: roleOf(x.pc, tri.root, tri.q) }; }),
        inverted: bass.pc !== tri.root, lesson: tri.trueChord ? LESSON.triad : LESSON.sus };
    }
    if (tri) { // 4+ pitch-classes that contain a triad → extended chord
      var ext = pcs.filter(function (p) { return triadPcs(tri.root, tri.q).indexOf(p) < 0; }).map(function (p) {
        var d2 = ((p - tri.root) % 12 + 12) % 12;
        return { 2: 'add9', 5: 'add11', 9: '6', 10: tri.q === 'major' ? '7' : 'm7', 11: 'maj7', 1: 'add♭9', 6: 'add♯11', 8: 'add♭13', 3: 'add♯9', 4: 'add3' }[d2] || 'add';
      });
      return { kind: 'extended', quality: tri.q, root: tri.root, trueChord: tri.trueChord, extensions: ext,
        label: '✨ ' + chordName(tri.root, tri.q) + ' (' + ext.join(' ') + '): a true chord with colour', lesson: LESSON.triad };
    }
    return { kind: 'cluster', label: (['', 'One', 'Two', 'Three', 'Four', 'Five'][pcs.length] || pcs.length) + ' voices, no chord yet', root: bass.pc, trueChord: false, lesson: LESSON.cluster };
  }
  /* the triad inside a pc-set (root on the bass when possible) — only called with 3+ pcs */
  function findTriad(pcs, bassPc) {
    var best = null;
    for (var r = 0; r < 12; r++) TRIADS.forEach(function (t, ti) {
      var tp = t.iv.map(function (i) { return (r + i) % 12; });
      if (!tp.every(function (p) { return pcs.indexOf(p) >= 0; })) return;
      var score = ti * 2 + (r === bassPc ? 0 : 5);
      if (!best || score < best.score) best = { root: r, q: t.q, trueChord: t.trueChord, score: score };
    });
    return best;
  }
  function inScales(tp, scales) {
    return scales.every(function (s) { var sc = SCALES[s.scale].map(function (i) { return (s.pc + i) % 12; }); return tp.every(function (p) { return sc.indexOf(p) >= 0; }); });
  }

  /* SUGGEST — ONE target chord for this cart.
     catalog: [{handle,title,ff,ex,available}] · attrs: harmony-map attrs · returns
     {state, target:{name,root,quality,trueChord}, need:[{role,pc,note,pick,alts}], alt:{name,swap:{role,pick}}, clash:[labels], hz, warn} */
  function suggest(cartItems, catalog, attrs) {
    var all = cartItems.map(function (i) { return voice(i, attrs); }).filter(Boolean);
    var g = groupHz(all), v = g.main, out = { hz: g.hz, warn: g.mixed ? 'hz' : null, voices: all.length, classify: classify(v) };
    if (!v.length) { out.state = 'empty'; return out; }
    var cls = out.classify, pcs = uniq(v.map(function (x) { return x.pc; }));
    var bass = v.slice().sort(function (a, b) { return a.midi - b.midi; })[0];
    var scales = v.filter(function (x) { return x.scale; });
    /* scale law: Dorian and Major-scale instruments never get sold as one chord set (canon) — warn, suggest nothing */
    if (uniq(scales.map(function (x) { return x.scale + x.pc; })).length > 1) { out.state = 'none'; out.warn = out.warn || 'scale'; return out; }
    /* completion gate: a finished true chord (or its extension) is celebrated — no chord tones are sold on top */
    if ((cls.kind === 'triad' || cls.kind === 'extended') && cls.trueChord) {
      out.state = 'complete'; out.target = { name: chordName(cls.root, cls.quality), root: cls.root, quality: cls.quality, trueChord: true }; out.need = [];
      return out;
    }
    var inCart = cartItems.map(function (i) { return i.handle; });
    var pool = catalog.map(function (c) { return voice(c, attrs) && Object.assign(voice(c, attrs), { available: c.available, now: !!c.now }); })
      .filter(function (c) { return c && c.available && !c.ex && !c.scale && c.hz === g.hz && inCart.indexOf(c.handle) < 0 && FORM_PREF[c.ff] != null; });
    function pickFor(pc, anchorMidi) {
      var cands = pool.filter(function (c) { return c.pc === pc; });
      if (!cands.length) return null;
      cands.sort(function (a, b) { return cost(a) - cost(b) || shelf(a, b); });   /* ③ shelf-first: a tie goes to Ships Now stock */
      function cost(c) { var d = c.midi - anchorMidi; return (d > 0 && d <= 12 ? 0 : d > 12 ? 4 + (d - 12) / 12 : 6 + (-d) / 12) + FORM_PREF[c.ff] * 0.5; }
      return cands;
    }
    /* candidate targets: every major/minor triad containing ALL cart pitch-classes (sus only if none) */
    function targets(qs) {
      var t = [];
      for (var r = 0; r < 12; r++) qs.forEach(function (q) {
        var tp = triadPcs(r, q);
        var hit = pcs.filter(function (p) { return tp.indexOf(p) >= 0; });
        if (!hit.length || !inScales(tp, scales)) return;
        t.push({ root: r, q: q, tp: tp, hit: hit.length, full: hit.length === pcs.length });
      });
      return t;
    }
    var T = targets(['major', 'minor']).filter(function (t) { return t.full; });
    if (!T.length) T = targets(['sus2', 'sus4']).filter(function (t) { return t.full; });
    var clashing = false;
    if (!T.length) { clashing = true; T = targets(['major', 'minor']).filter(function (t) { return t.tp.indexOf(bass.pc) >= 0; }); }
    var scored = T.map(function (t) {
      var anchor = v.filter(function (x) { return x.pc === t.root; })[0] || bass;
      var need = t.tp.filter(function (p) { return pcs.indexOf(p) < 0; }).map(function (p) {
        var c = pickFor(p, anchor.midi); return { role: roleOf(p, t.root, t.q), pc: p, note: spell(p, t.root, t.q), pick: c && c[0], alts: c ? c.slice(1, 3) : [] };
      });
      if (need.some(function (n) { return !n.pick; })) return null;
      var bassRole = roleOf(bass.pc, t.root, t.q);
      var s = (t.q === 'major' || t.q === 'minor' ? 0 : 6) + need.length * 2 + (bassRole === 'root' ? 0 : bassRole === 'fifth' ? 3 : 4) +
        need.reduce(function (a, n) { var d = n.pick.midi - anchor.midi; return a + (d > 0 && d <= 12 ? 0 : 2) + FORM_PREF[n.pick.ff] * 0.3; }, 0) - t.hit;
      return { t: t, need: need, s: s };
    }).filter(Boolean).sort(function (a, b) { return a.s - b.s || canon(a.t) - canon(b.t) || (a.t.q === 'minor' ? -1 : 1); });
    if (!scored.length) { out.state = 'none'; return out; }
    var best = scored[0];
    out.state = clashing ? 'clash' : 'build';
    out.target = { name: chordName(best.t.root, best.t.q), root: best.t.root, quality: best.t.q, trueChord: best.t.q === 'major' || best.t.q === 'minor' };
    out.need = best.need;
    out.have = v.map(function (x) { return { label: x.label, role: best.t.tp.indexOf(x.pc) >= 0 ? roleOf(x.pc, best.t.root, best.t.q) : 'outside' }; });
    if (clashing) out.clash = v.filter(function (x) { return best.t.tp.indexOf(x.pc) < 0; }).map(function (x) { return x.label; });
    /* the third decides the mood: offer the sibling (same root, other colour) as ONE swap — still one chord at a time */
    if (best.t.q === 'major' || best.t.q === 'minor') {
      var sib = scored.filter(function (x) { return x.t.root === best.t.root && x.t.q !== best.t.q && (x.t.q === 'major' || x.t.q === 'minor'); })[0];
      var third = sib && sib.need.filter(function (n) { return n.role === 'third'; })[0];
      if (third && best.need.some(function (n) { return n.role === 'third'; })) out.alt = { name: chordName(sib.t.root, sib.t.q), quality: sib.t.q, swap: third };
    }
    return out;
  }

  /* 🎹 PDP "Play your chord" (FULL SEND 10-16): the chord built ON this key as root.
     quality = 'major'|'minor' (default: the canon chord for this root, else major) · scale flutes keep their mode
     tones already in the bag (same Hz, same pitch-class) are marked, never re-sold · completion gate · octave twin LAST ·
     "Stack it small" = pocket/triple forms of the chord's tones. Pure: same catalog/attrs contract as suggest(). */
  function chordFor(anchor, quality, cartItems, catalog, attrs) {
    var me = voice(anchor, attrs); if (!me) return null;
    var q = quality === 'minor' || quality === 'major' ? quality : (CANON[me.pc] || 'major');
    var scales = me.scale ? [me] : [];
    var bagV = (cartItems || []).map(function (i) { return voice(i, attrs); }).filter(Boolean);
    bagV.forEach(function (b) { if (b.scale && b.hz === me.hz) scales.push(b); });
    if (!inScales(triadPcs(me.pc, q), scales)) { q = q === 'major' ? 'minor' : 'major'; if (!inScales(triadPcs(me.pc, q), scales)) return null; }
    var other = q === 'major' ? 'minor' : 'major', altOk = inScales(triadPcs(me.pc, other), scales);
    var inCart = (cartItems || []).map(function (i) { return i.handle; });
    var pool = (catalog || []).map(function (c) { var v = voice(c, attrs); return v && Object.assign(v, { available: c.available, now: !!c.now }); })
      .filter(function (c) { return c && c.available && !c.ex && !c.scale && c.hz === me.hz && c.handle !== me.handle && inCart.indexOf(c.handle) < 0 && FORM_PREF[c.ff] != null; });
    function near(pc, forms) {
      return pool.filter(function (c) { return c.pc === pc && (!forms || forms.indexOf(c.ff) >= 0); }).sort(function (a, b) {
        function cost(c) { var d = c.midi - me.midi; return (d > 0 && d <= 12 ? 0 : d > 12 ? 4 + (d - 12) / 12 : 6 + (-d) / 12) + FORM_PREF[c.ff] * 0.5; }
        return cost(a) - cost(b) || shelf(a, b);
      });
    }
    var bagHz = bagV.filter(function (b) { return b.hz === me.hz; });
    var tones = triadPcs(me.pc, q).map(function (pc) {
      var t = { pc: pc, role: roleOf(pc, me.pc, q), note: spell(pc, me.pc, q) };
      if (pc === me.pc) { t.have = 'page'; t.midi = me.midi; t.label = me.label; return t; }
      var b = bagHz.filter(function (x) { return x.pc === pc; })[0];
      if (b) { t.have = 'bag'; t.midi = b.midi; t.label = b.label; return t; }
      var c = near(pc); t.pick = c[0] || null; t.alts = c.slice(1, 3); if (t.pick) { t.midi = t.pick.midi; t.label = t.pick.label; }
      return t;
    });
    var need = tones.filter(function (t) { return !t.have; });
    var twin = pool.filter(function (c) { return c.pc === me.pc && c.midi !== me.midi; })
      .sort(function (a, b) { return Math.abs(a.midi - me.midi) - Math.abs(b.midi - me.midi) || FORM_PREF[a.ff] - FORM_PREF[b.ff] || shelf(a, b); })[0] || null;
    var picked = need.map(function (t) { return t.pick && t.pick.handle; }).concat(twin ? [twin.handle] : []);
    var small = [];
    /* Deck clutter rule ④: never the same voice twice on one screen — a pocket voice already on the page (this key itself, or a card
       above) is not re-offered */
    var shownSmall = need.concat(twin ? [{ pick: twin }] : [], [{ pick: me }]).filter(function (t) { return t.pick && t.pick.ff === 'pocket'; }).map(function (t) { return t.pick.label; });
    triadPcs(me.pc, q).forEach(function (pc) { near(pc, ['pocket']).forEach(function (c) {
      if (picked.indexOf(c.handle) < 0 && shownSmall.indexOf(c.label) < 0 && small.length < 3 && !small.some(function (x) { return x.label === c.label; })) small.push(c); }); });
    return { root: me.pc, rootLabel: me.label, hz: me.hz, quality: q, name: chordName(me.pc, q), alt: altOk ? { quality: other, name: chordName(me.pc, other) } : null,
      tones: tones, need: need, complete: need.length === 0, buyable: need.every(function (t) { return !!t.pick; }), twin: twin, small: small,
      midis: tones.map(function (t) { return t.midi; }).filter(function (m) { return m != null; }) };
  }

  return { chordFor: chordFor, parsePitch: parsePitch, hzOf: hzOf, scaleOf: scaleOf, voice: voice, classify: classify, suggest: suggest,
    chordName: chordName, spell: spell, display: display, LESSON: LESSON, IV: IV };
});
